import { useState } from 'react';
import {
  Outlet,
  Link,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { ChevronDown, Menu, X } from 'lucide-react';

import logo from '@/assets/images/two-bear-capital-horizontal.svg';
import { DarkModeToggle } from '@/components/DarkModeToggle';
import { Footer } from '@/components/Footer';
import { YearThemeProvider } from '@/components/YearThemeProvider';
import { useDarkModeInit } from '@/hooks/useDarkMode';
import {
  CURRENT_YEAR,
  getAllYears,
  getEvent,
  isValidYear,
} from '@/data/events';

// The native arrow ignores padding and sits flush against the border, so hide it
// and draw our own with matching right padding.
function YearSelect({
  value,
  years,
  onChange,
  className = '',
}: {
  value: number;
  years: number[];
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="focus:ring-primary-500 w-full appearance-none rounded-md border border-gray-300 bg-white py-1.5 pr-8 pl-3 text-sm font-medium text-gray-700 shadow-sm focus:ring-2 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
        aria-label="Select event year"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute top-1/2 right-2.5 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400"
        aria-hidden="true"
      />
    </div>
  );
}

export function RootLayout() {
  useDarkModeInit();
  const location = useLocation();
  const navigate = useNavigate();
  const { year } = useParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // The year in the URL when on an event page; otherwise the current event.
  const activeYear =
    year && isValidYear(Number(year)) ? Number(year) : CURRENT_YEAR;
  const years = getAllYears();
  // Theme the whole chrome (nav + content) by the active year. Falls back to the
  // current event on global routes that have no :year (e.g. the 404 page).
  const activeEvent = (getEvent(activeYear) ?? getEvent(CURRENT_YEAR))!;
  const activeTheme = activeEvent.theme;

  const isActive = (path: string, end = false) => {
    if (end) {
      return location.pathname === path;
    }
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const navLinks = [
    { path: `/${activeYear}`, label: 'Home', end: true },
    { path: `/${activeYear}/agenda`, label: 'Agenda' },
    { path: `/${activeYear}/logistics`, label: 'Logistics' },
  ];

  const onYearChange = (value: string, closeMenu = false) => {
    if (closeMenu) setIsMobileMenuOpen(false);
    navigate(`/${value}`);
  };

  return (
    <YearThemeProvider theme={activeTheme}>
      <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
        <nav className="bg-white shadow-xs dark:bg-gray-800">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-20 items-center justify-between">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center gap-3">
                  <img
                    src={logo}
                    alt="Two Bear Capital"
                    width={168}
                    height={40}
                    className="h-10 w-auto"
                  />
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex md:items-center md:space-x-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isActive(link.path, link.end)
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Right side: Dark Mode Toggle + Year switcher + Mobile Menu Button */}
              <div className="flex items-center gap-4">
                <DarkModeToggle />

                <YearSelect
                  value={activeYear}
                  years={years}
                  onChange={(value) => onYearChange(value)}
                  className="hidden md:block"
                />

                {/* Mobile menu button */}
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 md:hidden dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-expanded={isMobileMenuOpen}
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-8 w-8" aria-hidden="true" />
                  ) : (
                    <Menu className="h-8 w-8" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Navigation Menu */}
            {isMobileMenuOpen && (
              <div className="border-t border-gray-200 md:hidden dark:border-gray-700">
                <div className="space-y-1 px-2 pt-2 pb-3">
                  <div className="px-3 pt-1 pb-2">
                    <span
                      className="mb-1 block text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400"
                      aria-hidden="true"
                    >
                      Event Year
                    </span>
                    <YearSelect
                      value={activeYear}
                      years={years}
                      onChange={(value) => onYearChange(value, true)}
                      className="w-full"
                    />
                  </div>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block rounded-md px-3 py-2 text-base font-medium transition-colors ${
                        isActive(link.path, link.end)
                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer wifi={activeEvent.logistics.wifi} />
      </div>
    </YearThemeProvider>
  );
}
